import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  MessageSquare, ChevronLeft, ChevronRight, Filter, Search,
  Clock, CheckCircle, AlertCircle, XCircle, Send, ArrowLeft
} from 'lucide-react';

const statusConfig = {
  open: { label: 'Baru', color: 'bg-blue-100 text-blue-800', icon: AlertCircle },
  in_progress: { label: 'Diproses', color: 'bg-amber-100 text-amber-800', icon: Clock },
  resolved: { label: 'Selesai', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  closed: { label: 'Ditutup', color: 'bg-slate-100 text-slate-600', icon: XCircle },
};
const priorityConfig = {
  low: { label: 'Rendah', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Sedang', color: 'bg-amber-100 text-amber-700' },
  high: { label: 'Tinggi', color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700' },
};

export default function SupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const limit = 20;

  document.title = 'Tiket Support — Tokomoe';

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: (page - 1) * limit, limit };
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (searchQuery) params.search = searchQuery;
      const { data } = await api.get('/tenant/support-tickets', { params });
      setTickets(data.tickets || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, priorityFilter, searchQuery]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/support-tickets/stats');
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const openTicketDetail = async (ticketId) => {
    try {
      const { data } = await api.get(`/tenant/support-tickets/${ticketId}`);
      setTicketDetail(data.ticket);
      setSelectedTicket(ticketId);
      setReplyText('');
    } catch (err) {
      console.error('Failed to load ticket:', err);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await api.post(`/tenant/support-tickets/${selectedTicket}/reply`, { message: replyText });
      setReplyText('');
      await openTicketDetail(selectedTicket);
      fetchStats();
    } catch (err) {
      console.error('Failed to send reply:', err);
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (ticketId, status) => {
    try {
      await api.put(`/tenant/support-tickets/${ticketId}/status`, { status });
      fetchTickets();
      fetchStats();
      if (ticketDetail && ticketDetail.id === ticketId) {
        setTicketDetail(prev => ({ ...prev, status }));
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const totalPages = Math.ceil(total / limit);
  const fmtDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Detail View
  if (selectedTicket && ticketDetail) {
    const sc = statusConfig[ticketDetail.status] || statusConfig.open;
    const pc = priorityConfig[ticketDetail.priority] || priorityConfig.low;
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={() => { setSelectedTicket(null); setTicketDetail(null); }}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Kembali ke daftar tiket
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-lg">{ticketDetail.subject}</CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Dari: <span className="font-medium">{ticketDetail.customer_name || ticketDetail.customer_email || 'Customer'}</span>
                  &nbsp;·&nbsp;{fmtDate(ticketDetail.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${pc.color}`}>{pc.label}</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${sc.color}`}>{sc.label}</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Original message */}
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm">{ticketDetail.message}</p>
            </div>

            {/* Replies */}
            {ticketDetail.replies?.map(reply => (
              <div key={reply.id} className={`rounded-lg p-4 ${reply.role === 'tenant' ? 'bg-blue-50 ml-8' : 'bg-slate-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${reply.role === 'tenant' ? 'bg-blue-200 text-blue-800' : 'bg-slate-200 text-slate-700'}`}>
                    {reply.role === 'tenant' ? 'Anda' : 'Customer'}
                  </span>
                  <span className="text-xs text-slate-400">{reply.name || ''}</span>
                  <span className="text-xs text-slate-400">{fmtDate(reply.created_at)}</span>
                </div>
                <p className="text-sm">{reply.message}</p>
              </div>
            ))}

            {/* Reply Form */}
            {ticketDetail.status !== 'closed' && (
              <div className="border-t pt-4 space-y-3">
                <textarea rows={3} placeholder="Tulis balasan..." value={replyText} onChange={e => setReplyText(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <select value={ticketDetail.status} onChange={e => updateStatus(ticketDetail.id, e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm bg-white">
                      <option value="open">Baru</option>
                      <option value="in_progress">Diproses</option>
                      <option value="resolved">Selesai</option>
                      <option value="closed">Ditutup</option>
                    </select>
                  </div>
                  <Button size="sm" onClick={sendReply} disabled={sending || !replyText.trim()}>
                    <Send className="w-4 h-4 mr-1" /> {sending ? 'Mengirim...' : 'Kirim Balasan'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Tiket Support</h1>
        <p className="text-slate-500">Kelola tiket bantuan dari pelanggan</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total', count: stats.total, icon: MessageSquare, bg: 'bg-slate-100', fg: 'text-slate-600' },
            { label: 'Baru', count: stats.open, icon: AlertCircle, bg: 'bg-blue-100', fg: 'text-blue-600' },
            { label: 'Diproses', count: stats.in_progress, icon: Clock, bg: 'bg-amber-100', fg: 'text-amber-600' },
            { label: 'Selesai', count: stats.resolved, icon: CheckCircle, bg: 'bg-green-100', fg: 'text-green-600' },
            { label: 'Ditutup', count: stats.closed, icon: XCircle, bg: 'bg-slate-100', fg: 'text-slate-500' },
          ].map((s, i) => (
            <Card key={i}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2 rounded-lg ${s.bg}`}><s.icon className={`w-5 h-5 ${s.fg}`} /></div>
                <div>
                  <p className="text-xs text-slate-500">{s.label}</p>
                  <p className="text-xl font-bold">{s.count}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Filter className="w-4 h-4" /> Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              className="border rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Semua Status</option>
              {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={priorityFilter} onChange={e => { setPriorityFilter(e.target.value); setPage(1); }}
              className="border rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Semua Prioritas</option>
              {Object.entries(priorityConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" placeholder="Cari subjek..." value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
                className="border rounded-lg pl-9 pr-3 py-2 text-sm w-full" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Belum ada tiket support</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Subjek</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Pelanggan</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Prioritas</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Balasan</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Tanggal</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {tickets.map(t => {
                    const sc = statusConfig[t.status] || statusConfig.open;
                    const pc = priorityConfig[t.priority] || priorityConfig.low;
                    return (
                      <tr key={t.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openTicketDetail(t.id)}>
                        <td className="px-4 py-3 font-medium text-slate-900 max-w-[200px] truncate">{t.subject}</td>
                        <td className="px-4 py-3">
                          <div className="text-slate-700">{t.customer_name || '-'}</div>
                          <div className="text-xs text-slate-400">{t.customer_email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${pc.color}`}>{pc.label}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${sc.color}`}>{sc.label}</span>
                        </td>
                        <td className="px-4 py-3 text-slate-500">{t.replies_count || 0}</td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(t.created_at)}</td>
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <select value={t.status} onChange={e => updateStatus(t.id, e.target.value)}
                            className="border rounded px-2 py-1 text-xs bg-white">
                            <option value="open">Baru</option>
                            <option value="in_progress">Diproses</option>
                            <option value="resolved">Selesai</option>
                            <option value="closed">Ditutup</option>
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">{total} tiket</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-600">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
